import { styled } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { toast as sonnerToast } from 'sonner';

export type ToastVariant = 'success' | 'error' | 'info';

type SystemToastProps = {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
};

const variantStyles = {
  success: {
    bg: '#F3F9EE',
    border: '#85C553',
    color: '#85C553',
    Icon: CheckCircleOutlineIcon,
  },
  error: {
    bg: '#FFF1F1',
    border: '#942020',
    color: '#942020',
    Icon: ErrorOutlineIcon,
  },
  info: {
    bg: '#F5EFFA',
    border: '#663497',
    color: '#663497',
    Icon: InfoOutlinedIcon,
  },
};

const ToastContainer = styled(Paper)<{ bg: string; border: string }>(({ bg, border, theme }) => {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: bg,
    borderLeft: `4px solid ${border}`,
    borderRadius: 12,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(2),
    minWidth: '370px',
    maxWidth: 420,
  };
});

const IconWrapper = styled('div')<{ color: string }>(({ color }) => {
  return {
    marginRight: 12,
    marginTop: 2,
    color,
    flexShrink: 0,
  };
});

const Title = styled(Typography)(() => {
  return {
    color: '#2B163F',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '128%',
  };
});

const Description = styled(Typography)(() => {
  return {
    color: '#444444',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '136%',
    marginTop: '4px',
  };
});

export const SystemToast = ({ id, title, description, variant }: SystemToastProps) => {
  const { bg, border, color, Icon } = variantStyles[variant];

  return (
    <ToastContainer elevation={3} bg={bg} border={border}>
      <IconWrapper color={color}>
        <Icon fontSize="medium" />
      </IconWrapper>
      <Box flex="1">
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
      </Box>
      <IconButton
        size="small"
        onClick={() => {
          return sonnerToast.dismiss(id);
        }}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </ToastContainer>
  );
};

export const showSystemToast = (variant: ToastVariant, title: string, description?: string) => {
  sonnerToast.custom(
    (id: any) => {
      return <SystemToast id={id} title={title} description={description} variant={variant} />;
    },
    { duration: 3000 }
  );
};
